import { ConfigExemptionInfo } from 'components/Integrations/Groups/MS_MFA/IntegrationConfig_MS_MFA';
import { TFunction } from 'i18next';
import { IntegrationConfigBase } from 'models/integrations/integrationConfigBase';
import { IIntegrationEvidence } from 'models/integrations/integrationEvidence';
import { toLocaleDateTime } from 'utils/datetime';

//
// The administrative roles of CIS 5.2.2.1 by role template id. Mirrors the API's
// REDLAB.ISOPlanner.Core/Services/IntegrationManager/Integrations/MS_EntraBaseline/EntraAdministratorRoles.cs;
// change both together. The names are Microsoft's role names and are not translated.
//
export const entraAdministratorRoles: { id: string; name: string }[] = [
  { id: '9b895d92-2cd3-44c7-9d02-a6ac2d5ea5c3', name: 'Application Administrator' },
  { id: 'c4e39bd9-1100-46d3-8c65-fb160da0071f', name: 'Authentication Administrator' },
  { id: 'b0f54661-2d74-4c50-afa3-1ec803f12efe', name: 'Billing Administrator' },
  { id: '158c047a-c907-4556-b7ef-446551a6b5f7', name: 'Cloud Application Administrator' },
  { id: 'b1be1c3e-b65d-4f19-8427-f6fa0d97feb9', name: 'Conditional Access Administrator' },
  { id: '29232cdf-9323-42fd-ade2-1d097af3e4de', name: 'Exchange Administrator' },
  { id: '62e90394-69f5-4237-9190-012177145e10', name: 'Global Administrator' },
  { id: 'f2ef992c-3afb-46b9-b7cf-a126ee74c451', name: 'Global Reader' },
  { id: '729827e3-9c14-49f7-bb1b-9608f156bbb8', name: 'Helpdesk Administrator' },
  { id: '966707d0-3269-4727-9be2-8c3a10f19b9d', name: 'Password Administrator' },
  { id: '7be44c8a-adaf-4e2a-84d6-ab2649e08a13', name: 'Privileged Authentication Administrator' },
  { id: 'e8611ab8-c189-46e8-94e1-60213ab1f814', name: 'Privileged Role Administrator' },
  { id: '194ae4cb-b126-40b2-bd5b-6091b380977d', name: 'Security Administrator' },
  { id: 'f28a1f50-f6e7-4571-818b-6a12f2af6b6c', name: 'SharePoint Administrator' },
  { id: 'fe930be7-5e62-47db-91af-98c3a49a38b1', name: 'User Administrator' },
];

export const getEntraRoleName = (roleId: string): string =>
  entraAdministratorRoles.find((role) => role.id.toLowerCase() === roleId.toLowerCase())?.name ?? roleId;

//
// Config class
//
export class IntegrationConfig_MS_CAAdminMFA extends IntegrationConfigBase {
  requiredRoleIds: string[];

  //a policy for all users also covers the administrative roles
  acceptAllUsersPolicy: boolean;

  //the emergency access accounts a policy may exclude without a warning
  allowedExcludedUserIds: ConfigExemptionInfo[];

  createChecklistItemPerProblem: boolean;

  constructor() {
    super();
    this.requiredRoleIds = entraAdministratorRoles.map((role) => role.id);
    this.acceptAllUsersPolicy = true;
    this.allowedExcludedUserIds = [];
    this.createChecklistItemPerProblem = true;
  }
}

//
// Evidence classes
//
export class MS_CAAdminMFAProblem {
  code: string = '';

  //the English text of the API, shown for a code the web does not know
  text: string = '';

  values: string[] = [];
}

export class MS_CAAdminMFACandidate {
  id: string = '';

  name: string = '';

  //the Graph value: enabled, disabled or enabledForReportingButNotEnforced
  state: string = '';

  problems: MS_CAAdminMFAProblem[] = [];

  pass: boolean = false;
}

export class MS_CAAdminMFAExclusions {
  users: { id: string; allowed: boolean }[] = [];

  groups: string[] = [];

  roles: string[] = [];
}

export class MS_CAAdminMFAPrunedConfig {
  roleIds: string[] = [];

  userIds: string[] = [];
}

export class MS_CAAdminMFAEvidence implements IIntegrationEvidence {
  isCapped: boolean = false;

  evaluatedAtUtc: Date = new Date();

  policyCount: number = 0;

  candidates: MS_CAAdminMFACandidate[] = [];

  winningPolicyId?: string;

  winningPolicyName?: string;

  exclusions: MS_CAAdminMFAExclusions = new MS_CAAdminMFAExclusions();

  //the template ids of the roles the best failing candidate misses
  missingRoles: string[] = [];

  prunedConfig: MS_CAAdminMFAPrunedConfig = new MS_CAAdminMFAPrunedConfig();

  pass: boolean = false;

  toMarkdownReport(t: TFunction<['translation', 'integrations']>): string {
    const sb: string[] = [];
    const typedT = t as TFunction<string[]>;

    sb.push(
      `## ${t('integrations:MS_CAAdminMFA.Report.SummaryHeading', { date: toLocaleDateTime(this.evaluatedAtUtc) })}`,
    );
    sb.push(`- ${getCAAdminMFAOutcomeText(this, typedT)}`);
    sb.push(`- ${t('integrations:MS_CAAdminMFA.Report.PolicyCount', { number: this.policyCount })}`);
    if (this.isCapped) sb.push(`- ${t('integrations:MS_CAAdminMFA.Report.Capped')}`);

    if (this.candidates.length > 0) {
      sb.push('');
      sb.push(
        `| ${t('integrations:MS_CAAdminMFA.Columns.Policy')} | ${t('integrations:MS_CAAdminMFA.Columns.State')} | ${t(
          'integrations:MS_CAAdminMFA.Columns.Problems',
        )} | ${t('integrations:MS_CAAdminMFA.Columns.Result')} |`,
      );
      sb.push('| --- | --- | --- | --- |');
      this.candidates.forEach((candidate) => {
        const problems = candidate.problems.map((problem) => getCAAdminMFAProblemText(problem, typedT)).join('; ');
        const result = candidate.pass
          ? t('integrations:MS_CAAdminMFA.Result.Pass')
          : t('integrations:MS_CAAdminMFA.Result.Fail');
        sb.push(
          `| ${escapeCell(candidate.name)} | ${getCAAdminMFAStateText(candidate.state, typedT)} | ${escapeCell(
            problems,
          )} | ${result} |`,
        );
      });
    }

    if (this.pass) {
      sb.push('');
      sb.push(
        `### ${t('integrations:MS_CAAdminMFA.Report.ExclusionsHeading', { policy: this.winningPolicyName ?? '' })}`,
      );
      getCAAdminMFAExclusionLines(this, typedT).forEach((line) => sb.push(`- ${line}`));
    }

    return sb.join('\n');
  }
}

const escapeCell = (value: string): string => value.replace(/\|/g, '\\|');

//
// Texts shared by the panel and the report
//
export const getCAAdminMFAOutcomeText = (evidence: MS_CAAdminMFAEvidence, t: TFunction<string[]>): string => {
  if (evidence.pass) {
    return t('integrations:MS_CAAdminMFA.Outcome.Pass', { policy: evidence.winningPolicyName ?? '' });
  }

  if (evidence.candidates.length === 0) return t('integrations:MS_CAAdminMFA.Outcome.NoCandidate');

  return t('integrations:MS_CAAdminMFA.Outcome.Fail');
};

export const getCAAdminMFAStateText = (state: string, t: TFunction<string[]>): string => {
  switch (state) {
    case 'enabled':
      return t('integrations:MS_CAAdminMFA.State.Enabled');
    case 'disabled':
      return t('integrations:MS_CAAdminMFA.State.Disabled');
    case 'enabledForReportingButNotEnforced':
      return t('integrations:MS_CAAdminMFA.State.ReportOnly');
    default:
      return state;
  }
};

//the values are ids: the role ids are named through the role table, other ids are shown as given
const getProblemValues = (problem: MS_CAAdminMFAProblem): string => {
  if (problem.code === 'RolesNotIncluded' || problem.code === 'RequiredRoleExcluded') {
    return problem.values.map((value) => getEntraRoleName(value)).join(', ');
  }

  return problem.values.join(', ');
};

export const getCAAdminMFAProblemText = (problem: MS_CAAdminMFAProblem, t: TFunction<string[]>): string => {
  const values = getProblemValues(problem);

  //translation keys cannot be built from variables, so every code has its own case
  switch (problem.code) {
    case 'Disabled':
      return t('integrations:MS_CAAdminMFA.Problems.Disabled');
    case 'NotAllUsers':
      return t('integrations:MS_CAAdminMFA.Problems.NotAllUsers');
    case 'RolesNotIncluded':
      return t('integrations:MS_CAAdminMFA.Problems.RolesNotIncluded', { values });
    case 'RequiredRoleExcluded':
      return t('integrations:MS_CAAdminMFA.Problems.RequiredRoleExcluded', { values });
    case 'NotAllApps':
      return t('integrations:MS_CAAdminMFA.Problems.NotAllApps');
    case 'DoesNotTarget':
      return t('integrations:MS_CAAdminMFA.Problems.DoesNotTarget', { values });
    case 'TargetExcluded':
      return t('integrations:MS_CAAdminMFA.Problems.TargetExcluded', { values });
    case 'AppsExcluded':
      return t('integrations:MS_CAAdminMFA.Problems.AppsExcluded', { values });
    case 'PlatformsLimited':
      return t('integrations:MS_CAAdminMFA.Problems.PlatformsLimited');
    case 'LocationsLimited':
      return t('integrations:MS_CAAdminMFA.Problems.LocationsLimited');
    case 'DeviceFilter':
      return t('integrations:MS_CAAdminMFA.Problems.DeviceFilter');
    case 'ClientAppTypesLimited':
      return t('integrations:MS_CAAdminMFA.Problems.ClientAppTypesLimited');
    case 'UserRiskCondition':
      return t('integrations:MS_CAAdminMFA.Problems.UserRiskCondition');
    case 'SignInRiskCondition':
      return t('integrations:MS_CAAdminMFA.Problems.SignInRiskCondition');
    case 'AuthenticationFlowsLimited':
      return t('integrations:MS_CAAdminMFA.Problems.AuthenticationFlowsLimited');
    case 'GrantMissing':
      return t('integrations:MS_CAAdminMFA.Problems.GrantMissing');
    case 'NoCandidate':
      return t('integrations:MS_CAAdminMFA.Problems.NoCandidate');
    default:
      return problem.text || problem.code;
  }
};

//the winning policy's exclusions: information, the allowed emergency accounts counted apart
export const getCAAdminMFAExclusionLines = (evidence: MS_CAAdminMFAEvidence, t: TFunction<string[]>): string[] => {
  const exclusions = evidence.exclusions;
  const allowed = exclusions.users.filter((user) => user.allowed).length;
  const other = exclusions.users.length - allowed;
  if (exclusions.users.length === 0 && exclusions.groups.length === 0 && exclusions.roles.length === 0) {
    return [t('integrations:MS_CAAdminMFA.Exclusions.None')];
  }

  const lines: string[] = [];
  if (allowed > 0) lines.push(t('integrations:MS_CAAdminMFA.Exclusions.AllowedUsers', { number: allowed }));
  if (other > 0) lines.push(t('integrations:MS_CAAdminMFA.Exclusions.OtherUsers', { number: other }));
  if (exclusions.groups.length > 0) {
    lines.push(t('integrations:MS_CAAdminMFA.Exclusions.Groups', { number: exclusions.groups.length }));
  }
  if (exclusions.roles.length > 0) {
    lines.push(
      t('integrations:MS_CAAdminMFA.Exclusions.Roles', {
        roles: exclusions.roles.map((roleId) => getEntraRoleName(roleId)).join(', '),
      }),
    );
  }

  return lines;
};

const toStringArray = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((item) => typeof item === 'string') : [];

export const parse_Evidence_MS_CAAdminMFA = (raw: string | undefined): MS_CAAdminMFAEvidence | undefined => {
  if (!raw) return undefined;

  const rawEvidence = JSON.parse(raw);
  if (!rawEvidence) return undefined;

  const evidence = new MS_CAAdminMFAEvidence();
  evidence.isCapped = rawEvidence.isCapped ?? false;
  evidence.evaluatedAtUtc = rawEvidence.evaluatedAtUtc ? new Date(rawEvidence.evaluatedAtUtc) : new Date();
  evidence.policyCount = rawEvidence.policyCount ?? 0;
  evidence.candidates = (Array.isArray(rawEvidence.candidates) ? rawEvidence.candidates : []).map(
    (rawCandidate: MS_CAAdminMFACandidate) => {
      const candidate = new MS_CAAdminMFACandidate();
      candidate.id = rawCandidate.id ?? '';
      candidate.name = rawCandidate.name ?? '';
      candidate.state = rawCandidate.state ?? '';
      candidate.pass = rawCandidate.pass ?? false;
      candidate.problems = (Array.isArray(rawCandidate.problems) ? rawCandidate.problems : []).map(
        (rawProblem: MS_CAAdminMFAProblem) => {
          const problem = new MS_CAAdminMFAProblem();
          problem.code = rawProblem.code ?? '';
          problem.text = rawProblem.text ?? '';
          problem.values = toStringArray(rawProblem.values);

          return problem;
        },
      );

      return candidate;
    },
  );
  evidence.winningPolicyId = rawEvidence.winningPolicyId ?? undefined;
  evidence.winningPolicyName = rawEvidence.winningPolicyName ?? undefined;
  evidence.exclusions.users = (Array.isArray(rawEvidence.exclusions?.users) ? rawEvidence.exclusions.users : []).map(
    (user: { id: string; allowed: boolean }) => ({ id: user.id ?? '', allowed: user.allowed === true }),
  );
  evidence.exclusions.groups = toStringArray(rawEvidence.exclusions?.groups);
  evidence.exclusions.roles = toStringArray(rawEvidence.exclusions?.roles);
  evidence.missingRoles = toStringArray(rawEvidence.missingRoles);
  evidence.prunedConfig.roleIds = toStringArray(rawEvidence.prunedConfig?.roleIds);
  evidence.prunedConfig.userIds = toStringArray(rawEvidence.prunedConfig?.userIds);
  evidence.pass = rawEvidence.pass ?? false;

  return evidence;
};
